// ============================================================
// FYUNOR — Country Configuration
// Reusable country model. Architecture only — minimal data per
// country. Phase 2 attaches tax/rule packs via locale/rules.
// ============================================================

/**
 * CountryConfig shape:
 * {
 *   code: string                 // ISO-3166 alpha-2
 *   name: string
 *   currency: string             // ISO-4217
 *   currencySymbol: string
 *   currencyPosition: "prefix" | "suffix"
 *   locale: string               // BCP-47
 *   decimalSeparator: string
 *   thousandsSeparator: string
 *   dateFormat: string          // pattern
 *   taxYearStart: { month, day } // fiscal year start
 *   regions: boolean             // supports states/provinces
 *   references: { name, url }[]
 *   supportedCalculators: string[] | ["*"]
 *   settings: Record<string, any>
 * }
 */
export function defineCountry(config) {
  return Object.freeze({
    decimalSeparator: ".",
    thousandsSeparator: ",",
    currencyPosition: "prefix",
    regions: false,
    supportedCalculators: ["*"],
    references: [],
    settings: {},
    ...config,
  });
}

export const COUNTRIES = {
  IE: defineCountry({
    code: "IE",
    name: "Ireland",
    currency: "EUR",
    currencySymbol: "€",
    locale: "en-IE",
    dateFormat: "DD/MM/YYYY",
    taxYearStart: { month: 1, day: 1 },
    references: [{ name: "Revenue Commissioners", url: "https://www.revenue.ie" }],
  }),
  GB: defineCountry({
    code: "GB",
    name: "United Kingdom",
    currency: "GBP",
    currencySymbol: "£",
    locale: "en-GB",
    dateFormat: "DD/MM/YYYY",
    taxYearStart: { month: 4, day: 6 },
    references: [{ name: "HM Revenue & Customs", url: "https://www.gov.uk/hmrc" }],
  }),
  US: defineCountry({
    code: "US",
    name: "United States",
    currency: "USD",
    currencySymbol: "$",
    locale: "en-US",
    dateFormat: "MM/DD/YYYY",
    taxYearStart: { month: 1, day: 1 },
    regions: true,
    references: [{ name: "IRS", url: "https://www.irs.gov" }],
  }),
  CA: defineCountry({
    code: "CA",
    name: "Canada",
    currency: "CAD",
    currencySymbol: "$",
    locale: "en-CA",
    dateFormat: "YYYY-MM-DD",
    taxYearStart: { month: 1, day: 1 },
    regions: true,
    references: [{ name: "CRA", url: "https://www.canada.ca/en/revenue-agency" }],
  }),
  AU: defineCountry({
    code: "AU",
    name: "Australia",
    currency: "AUD",
    currencySymbol: "$",
    locale: "en-AU",
    dateFormat: "DD/MM/YYYY",
    taxYearStart: { month: 7, day: 1 },
    references: [{ name: "ATO", url: "https://www.ato.gov.au" }],
  }),
};

export function getCountry(code) {
  return COUNTRIES[code?.toUpperCase()] || null;
}

export function isSupportedCountry(code) {
  return Boolean(getCountry(code));
}

export function listCountries() {
  return Object.values(COUNTRIES);
}